import type { PersonProfileData } from './types';
import { getPageTime } from './utils/performance';

/**
 * Counts the header actions that `HeaderActions` will render for the given profile.
 *
 * Lives in `analytics.ts` because it exists purely to populate the `numActions`
 * attribute on `ui.profilecard.rendered.content`; it has no rendering
 * responsibility and must run regardless of whether `HeaderActions` itself
 * is mounted yet (the link lookup for "View profile" may still be in flight
 * when the event fires).
 *
 * Visibility rules (mirrored from `HeaderActions`):
 * - "View profile" renders when `accountId` is present (resolved to an `href` downstream).
 * - "Third-party message" renders once when any third-party integration is present
 *   (a single button regardless of integration count).
 * - Deactivated accounts (`inactive` / `closed`) only render "View profile"; the third-party
 *   message button is suppressed, so it must not be counted here.
 */
export function countRenderedActions(profileData: PersonProfileData | undefined): number {
	if (!profileData) {
		return 0;
	}
	let count = 0;
	if (profileData.accountId) {
		count += 1;
	}
	const isDeactivated =
		profileData.accountStatus === 'inactive' || profileData.accountStatus === 'closed';
	if (
		!isDeactivated &&
		profileData.thirdPartyIntegrations &&
		profileData.thirdPartyIntegrations.length > 0
	) {
		count += 1;
	}
	return count;
}

/**
 * Error classifications emitted on `failed.request` and `rendered.error`.
 * Aligns with the SLO dashboards that filter on this attribute - keep it to
 * known values so dashboards do not silently bucket traffic under `undefined`.
 */
export type CardErrorType = 'NotFound' | 'default';

/**
 * Subset of error-shape info surfaced to the `failed.request` event,
 * extracted from a Relay/network error by `getErrorAttributes`.
 */
export type CardRequestErrorAttributes = {
	errorType: CardErrorType;
	statusCode?: number;
};

/**
 * Page-relative time in integer ms for the `firedAt` envelope on every
 * `profilecard.*` event. Rounded because the SLO pipeline buckets these
 * values and would discard sub-ms precision anyway.
 */
export const firedAt = (): number => Math.round(getPageTime());

/**
 * Classify an arbitrary thrown error into the SLO-friendly shape used by
 * `failed.request`. Returns `{ errorType: 'NotFound' }` for 404 / not-found
 * GraphQL errors, otherwise `{ errorType: 'default' }`. `statusCode` is
 * surfaced when the error carries an HTTP status.
 *
 * Defensive by design: input can be `unknown`, `Error`, a plain Relay error
 * object, or a thrown string. Fields are read only when they are the expected
 * type so a malformed error does not itself throw.
 */
export const getErrorAttributes = (error: unknown): CardRequestErrorAttributes => {
	const statusCode =
		readNumericField(error, 'statusCode') ??
		readNumericField(error, 'status') ??
		readRelayExtensionsStatusCode(error);

	if (statusCode === 404 || isNotFoundLikeError(error, statusCode)) {
		return statusCode !== undefined
			? { errorType: 'NotFound', statusCode }
			: { errorType: 'NotFound' };
	}

	return statusCode !== undefined ? { errorType: 'default', statusCode } : { errorType: 'default' };
};

/**
 * Relay surfaces network errors as `{ errors: [{ extensions: { statusCode, errorType, ... } }] }`.
 * Reads the first error's `extensions.statusCode` so the SLO classification works against the
 * common Relay error envelope, not just hand-rolled `{ statusCode }` objects.
 */
const readRelayExtensionsStatusCode = (error: unknown): number | undefined => {
	if (!error || typeof error !== 'object' || !('errors' in error)) {
		return undefined;
	}
	const errors = (error as { errors: unknown }).errors;
	if (!Array.isArray(errors) || errors.length === 0) {
		return undefined;
	}
	const first = errors[0];
	if (!first || typeof first !== 'object' || !('extensions' in first)) {
		return undefined;
	}
	const extensions = (first as { extensions: unknown }).extensions;
	if (!extensions || typeof extensions !== 'object') {
		return undefined;
	}
	return readNumericField(extensions, 'statusCode') ?? readNumericField(extensions, 'status');
};

const readNumericField = (error: unknown, field: string): number | undefined => {
	if (error && typeof error === 'object' && field in error) {
		const value = (error as Record<string, unknown>)[field];
		if (typeof value === 'number' && Number.isFinite(value)) {
			return value;
		}
	}
	return undefined;
};

/** Returns true for unambiguous not-found signals. Message-pattern matching is last-resort — only used when statusCode is absent to avoid misclassifying generic errors (e.g. a 500 whose message contains "not found"). */
const isNotFoundLikeError = (error: unknown, statusCode: number | undefined): boolean => {
	if (!error || typeof error !== 'object') {
		return false;
	}
	const code = readStringField(error, 'code');
	if (code && /NOT_FOUND/i.test(code)) {
		return true;
	}
	// Only match message patterns when there is no statusCode — a 500 with "not found" in its message must not be misclassified as NotFound.
	if (statusCode === undefined) {
		const message = readStringField(error, 'message');
		if (message && /\bnot\s*found\b/i.test(message)) {
			return true;
		}
	}
	return false;
};

const readStringField = (error: object, field: string): string | undefined => {
	if (field in error) {
		const value = (error as Record<string, unknown>)[field];
		if (typeof value === 'string') {
			return value;
		}
	}
	return undefined;
};
